import {createIndexedDbKeyValueStore} from '@Utils/indexed-db-store.util';
import type {DeepPartial} from '@/global';
import type {QRCodeItemObject} from '@PosterWhiteboard/items/qr-code-item.class';
const QR_HANDOFF_DB_NAME = 'pmwQrHandoff';
const QR_HANDOFF_STORE_NAME = 'handoff';
const QR_HANDOFF_KEY = 'pending';
/**
 * How long a stashed code stays adoptable. Short on purpose: the tool's page now stays open behind the editor's tab, so this is the window
 * in which every design opened from one visit picks the code up — not a lifetime for holding on to what somebody typed.
 */
const QR_HANDOFF_TTL_MS = 5 * 60 * 1000;

const handoffStore = createIndexedDbKeyValueStore(QR_HANDOFF_DB_NAME, QR_HANDOFF_STORE_NAME);

interface QRHandoffRecord {
  itemObject: DeepPartial<QRCodeItemObject>;
  createdAt: number;
}

const isHandoffRecord = (value: unknown): value is QRHandoffRecord => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record: Partial<QRHandoffRecord> = value;
  return typeof record.createdAt === 'number' && typeof record.itemObject === 'object' && record.itemObject !== null;
};

const clearQRHandoff = async (): Promise<void> => {
  try {
    await handoffStore.remove(QR_HANDOFF_KEY);
  } catch (error) {
    window.PMW.log('Could not clear the QR handoff', error);
  }
};

/**
 * Reading no longer takes the record away, so nothing else would: a payload nobody collected — a Wi-Fi password, a vCard — would otherwise
 * sit in the browser profile long after the visit. The timer is reset on each stash so only the newest one is ever waiting to be cleared.
 */
let clearHandoffTimeoutId: number | null = null;
const scheduleHandoffClear = (): void => {
  if (clearHandoffTimeoutId !== null) {
    window.clearTimeout(clearHandoffTimeoutId);
  }
  clearHandoffTimeoutId = window.setTimeout((): void => {
    clearHandoffTimeoutId = null;
    void clearQRHandoff();
  }, QR_HANDOFF_TTL_MS);
};

const isStaleRecord = (record: QRHandoffRecord): boolean => {
  return Date.now() - record.createdAt > QR_HANDOFF_TTL_MS;
};

const getLiveHandoffRecord = async (): Promise<QRHandoffRecord | null> => {
  const record = await handoffStore.get(QR_HANDOFF_KEY);
  if (!isHandoffRecord(record) || isStaleRecord(record)) {
    await clearQRHandoff();
    return null;
  }
  return record;
};

/**
 * The stash's timer only ever runs in the tab that wrote it, and the whole point of the record is that the visitor leaves that tab for the
 * editor. Closing the tool before the timer fires would strand a Wi-Fi password or a vCard in the browser profile with nothing left to
 * remove it, so whichever page pulls this module in next sweeps what the timer could not.
 */
const sweepStaleQRHandoff = async (): Promise<void> => {
  try {
    await getLiveHandoffRecord();
  } catch (error) {
    window.PMW.log('Could not sweep the QR handoff', error);
  }
};

void sweepStaleQRHandoff();

/**
 * Only a tab the tool itself opened adopts the stash. The record is deliberately left in place so several designs picked in one visit each
 * get the code, and without this marker that generosity reaches every other design too: open one of your own saved flyers within the TTL
 * and its QR would be rewritten to a code you never meant to put on it.
 */
const QR_HANDOFF_URL_PARAM = 'qrHandoff';

const withQRHandoffParam = (editorUrl: string): string => {
  const url = new URL(editorUrl, window.location.origin);
  url.searchParams.set(QR_HANDOFF_URL_PARAM, '1');
  return url.toString();
};

export const isQRHandoffTarget = (): boolean => {
  return new URLSearchParams(window.location.search).has(QR_HANDOFF_URL_PARAM);
};

const writeQRHandoff = async (itemObject: DeepPartial<QRCodeItemObject>): Promise<boolean> => {
  try {
    await handoffStore.put({itemObject, createdAt: Date.now()}, QR_HANDOFF_KEY);
    scheduleHandoffClear();
    return true;
  } catch (error) {
    window.PMW.log('Could not stash the QR handoff', error);
    return false;
  }
};

/**
 * Opens a design in a new tab with a code waiting for it. The tab opens before the stash is written so a pop-up blocker still counts the
 * click; it closes again if the stash fails, and without a tab of its own the editor loads in this one.
 */
export const openDesignWithQRHandoff = async (designUrl: string, itemObject: DeepPartial<QRCodeItemObject>): Promise<boolean> => {
  const editorTab = window.open('', '_blank');
  const wasHandedOff = await writeQRHandoff(itemObject);
  if (!wasHandedOff) {
    editorTab?.close();
    return false;
  }

  const editorUrl = withQRHandoffParam(designUrl);
  if (editorTab === null) {
    window.location.assign(editorUrl);
    return true;
  }
  editorTab.location.assign(editorUrl);
  return true;
};

/**
 * The record is left where it is rather than taken, because one visit can open several designs and each opens its own tab: taking it would
 * hand the code to whichever tab finished loading first and leave the rest with a design and no code on it. A stale one is cleared here.
 */
export const readQRHandoff = async (): Promise<DeepPartial<QRCodeItemObject> | null> => {
  try {
    return (await getLiveHandoffRecord())?.itemObject ?? null;
  } catch (error) {
    window.PMW.log('Could not read the QR handoff', error);
    return null;
  }
};
